import { AuthCurrentLoader } from '@framework/auth/components/AuthCurrentLoader.tsx';
import {
  useAuth,
  useOnRefreshTokenExpired,
} from '@framework/auth/state/AuthSlice.tsx';
import { useModalHistoryCleanup } from '@framework/ui/components/modal/useModalHistoryCleanup.tsx';
import {
  getLocationState,
  type ILocationState,
} from '@framework/ui/core/router/getLocationState.ts';
import {
  Redirect,
  Route,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useScrollToTopOnMount } from '@framework/ui/core/router/useScrollToTopOnMount.tsx';
import { useSmoothAnchorScroll } from '@framework/ui/core/router/useSmoothAnchorScroll.ts';
import { Switch, useLocation } from 'react-router';
import DossierRoutes from '#build-flags/routes/dossiers/Routes.tsx';
import RegisterRecordListPage from '#build-flags/routes/register-records/list/RegisterRecordListPage.tsx';
import ExtendedUserLoginRoutes from '#build-flags/routes/user/ExtendedUserLoginRoutes.tsx';
import VerifyPassRoutes from '#build-flags/routes/verify-pass/Routes.tsx';
import { AddressBookRoutes } from '#features/apps/frontoffice-web/src/routes/address-book/Routes.tsx';
import { FrontLoginRoutes } from '#theme';
import { NotFoundPage } from './errors/not-found/NotFoundPage.tsx';
import { InquiryCoverPage } from './inquiries/cover/InquiryCoverPage.tsx';
import { InquiryListPage } from './inquiries/list/InquiryListPage.tsx';
import { LoginPage } from './login/LoginPage.tsx';
import { NotificationListPage } from './menu/notification/NotificationListPage.tsx';
import { ObservationPage } from './observations/detail/ObservationPage.tsx';
import { ObservationsListPage } from './observations/list/ObservationsListPage.tsx';
import { UserSettingsPage } from './settings/UserSettingsPage.tsx';
import { DelegationPage } from './submission/delegation/DelegationPage.tsx';
import { SubmissionListPage } from './submission/SubmissionListPage.tsx';
import { SubmissionPage } from './submission/SubmissionPage.tsx';
import { SubmissionResultPage } from './submission/SubmissionResultPage.tsx';
import { SubmissionSentPage } from './submission/SubmissionSentPage.tsx';
import { VerificationPage } from './verification/VerificationPage.tsx';

export const Routes = () => {
  const { isLogged } = useAuth();

  const location = useLocation<ILocationState>();
  const { from } = getLocationState(location, '/domande');

  const history = useHistory();
  useOnRefreshTokenExpired(() => {
    history.push('/login', { ...(location.state ?? {}), from: location });
  });

  // Chiudiamo tutte le modal alla navigazione
  useModalHistoryCleanup(history);

  useSmoothAnchorScroll();
  useScrollToTopOnMount();

  return (
    <AuthCurrentLoader>
      {isLogged ? (
        <Switch>
          <Redirect from="/login" to={from} />
          <Redirect exact from="/" to="/domande" />
          <Route path="/verifica" component={VerificationPage} />
          <Route path="/domande/:id" component={InquiryCoverPage} />
          <Route exact path="/domande" component={InquiryListPage} />

          <Route exact path="/area-pubblica" component={ObservationsListPage} />

          <Route
            path="/area-pubblica/:id/moduli/:moduleId"
            component={ObservationPage}
          />
          {RegisterRecordListPage && (
            <Route
              exact
              path="/albo/lista/:id"
              component={RegisterRecordListPage}
            />
          )}
          <Route path="/area-pubblica/:id" component={ObservationPage} />

          <Route
            path="/richieste/:id/inviata/moduli/:moduleId"
            component={SubmissionSentPage}
          />

          <Route path="/richieste/:id/inviata" component={SubmissionSentPage} />
          <Route
            path="/richieste/:id/risultato"
            component={SubmissionResultPage}
          />
          <Route path="/richieste/:id/deleghe" component={DelegationPage} />

          {/* Non possiamo togliere questo path, altrimenti in SubmissionPage non si può accedere al moduleId dalle props */}
          {/* Inseriamo tutti i path possibili con gli ID per permettere al match di leggere le variabili dall'URL */}
          <Route
            path="/richieste/:id/moduli/:moduleId"
            component={SubmissionPage}
          />
          <Route path="/richieste/:id" component={SubmissionPage} />
          <Route exact path="/richieste" component={SubmissionListPage} />

          <Route path="/notifiche" component={NotificationListPage} />

          {AddressBookRoutes && (
            <Route path="/rubrica" component={AddressBookRoutes} />
          )}

          {DossierRoutes && (
            <Route path="/pratiche" component={DossierRoutes} />
          )}

          {/* Le preferenze di comunicazione non dipendono dagli account locali
              (valgono anche per gli utenti SPID/CIE): la route vive qui,
              indipendente dal build flag delle route estese */}
          {process.env.ENABLE_COMMUNICATION_PREFERENCES === 'true' && (
            <Route
              exact
              path="/utente/impostazioni"
              component={UserSettingsPage}
            />
          )}

          {ExtendedUserLoginRoutes && (
            <Route
              path="/utente"
              render={() => <ExtendedUserLoginRoutes isLogged={isLogged} />}
            />
          )}

          {VerifyPassRoutes && (
            <Route path="/verifica-tesserini" component={VerifyPassRoutes} />
          )}

          {/** Pagina non trovata */}
          <Route component={NotFoundPage} />
        </Switch>
      ) : (
        <Switch>
          <Redirect exact from="/" to="/domande" />
          {/* Routes extra */}
          {...FrontLoginRoutes}

          <Route exact path="/domande" component={InquiryListPage} />
          <Route path="/domande/:id" component={InquiryCoverPage} />
          <Route path="/verifica" component={VerificationPage} />
          <Route exact path="/area-pubblica" component={ObservationsListPage} />
          {RegisterRecordListPage && (
            <Route
              exact
              path="/albo/lista/:id"
              component={RegisterRecordListPage}
            />
          )}
          <Route
            path="/area-pubblica/:id/moduli/:moduleId"
            component={ObservationPage}
          />
          <Route path="/area-pubblica/:id" component={ObservationPage} />
          <Route path="/login" component={LoginPage} />
          {process.env.NODE_ENV === 'development' && (
            <Route path={'/frontoffice/login' as any} component={LoginPage} />
          )}

          <Route exact path="/login" component={LoginPage} />

          {ExtendedUserLoginRoutes && (
            <Route
              path="/utente"
              render={() => <ExtendedUserLoginRoutes isLogged={isLogged} />}
            />
          )}

          {VerifyPassRoutes && (
            <Route path="/verifica-tesserini" component={VerifyPassRoutes} />
          )}

          <Redirect
            to={{
              pathname: '/login',
              state: { ...(location.state ?? {}), from: location },
            }}
          />
        </Switch>
      )}
    </AuthCurrentLoader>
  );
};
